// frontend/packages/business-console/src/pages/UpryzzeAi.jsx
//
// UpRyzze AI - the conversational CMS (docs/design/upryzze-ai-cms.md).
// One chat-first surface where a client changes their live site by talking to
// UpRyzze AI. Phase 1 does copy edits end to end: find the page, gather the
// change, confirm, preview a grounded proposal, on approval send it to the
// tenant's PREVIEW (uat) site, then on a separate explicit action PUBLISH the
// reviewed draft to the live (prod) site. Promote is client-triggered and
// server-guarded (design section 6: a real pending diff + a not-broken preview
// endpoint before any prod merge).
//
// Gated per business server-side: the page calls /cms-ai/status and shows a
// friendly "not available" state for anyone not allowlisted. Role + feature
// gating is authoritative on the server; this is just UX.

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';

import { BusinessContext } from '../contexts/BusinessContext';
import AiUsageMeter from '../components/AiUsageMeter';
import {
  getCmsAiStatus,
  sendCmsAiMessage,
  applyCmsAiEdit,
  getCmsAiPreviewStatus,
  promoteCmsAi,
} from '../services/cmsAiService';

const INTRO =
  'Hi, I am UpRyzze AI. I can update the wording on your website pages. ' +
  'Tell me what you would like to change, for example "make the intro on my ' +
  'about page shorter" or "update the tagline on my home page". I will show ' +
  'you a preview before anything is saved.\n\n' +
  'Heads up: each request uses a small amount of AI credit, so keep an eye on ' +
  'your usage in the top right. If you would rather edit by hand, use the ' +
  '"Edit manually" button.';

function errMessage(err) {
  const status = err?.response?.status;
  const serverMsg = err?.response?.data?.message;
  if (status === 404) return 'UpRyzze AI is not turned on for this site yet.';
  if (status === 403) return 'Only site admins can use UpRyzze AI.';
  if (status === 429)
    return 'That was a lot of requests in a short time. Give it a minute and try again.';
  return serverMsg || 'Something went wrong. Please try again.';
}

// A preview of the proposed copy change with an approve action. Approving sends
// the change to the tenant's preview (uat) site; promoting the preview to the
// live site is a separate step (design section 6).
function ProposalPreview({ proposal, onApprove, busy, applied }) {
  if (
    !proposal ||
    !Array.isArray(proposal.changes) ||
    proposal.changes.length === 0
  ) {
    return null;
  }
  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1,
        p: 1.5,
        borderColor: 'primary.light',
        bgcolor: 'action.hover',
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: 'text.secondary' }}
      >
        Preview of the change
      </Typography>
      <Stack spacing={1} sx={{ mt: 0.75 }}>
        {proposal.changes.map((c) => (
          <Box key={c.field}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', textTransform: 'capitalize' }}
            >
              {String(c.field)}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {String(c.value)}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Divider sx={{ my: 1 }} />
      {applied ? (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <CheckCircleRoundedIcon color="success" fontSize="small" />
          <Typography
            variant="caption"
            sx={{ color: 'success.main', fontWeight: 700 }}
          >
            Sent to your preview site
          </Typography>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            onClick={onApprove}
            disabled={busy}
            startIcon={
              busy ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <CheckCircleRoundedIcon />
              )
            }
          >
            {busy ? 'Sending...' : 'Approve and send to preview'}
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Nothing goes live until you review it.
          </Typography>
        </Stack>
      )}
    </Paper>
  );
}

export default function UpryzzeAi() {
  const { business } = useContext(BusinessContext);
  const slug = business?.slug || '';

  const [statusLoading, setStatusLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);

  const [messages, setMessages] = useState([
    { role: 'assistant', content: INTRO },
  ]);
  const [convoState, setConvoState] = useState(null);
  const [pages, setPages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState(null);
  const [applyingIdx, setApplyingIdx] = useState(null);
  const [appliedIdx, setAppliedIdx] = useState(() => new Set());
  // Publish (promote uat -> prod) state. `appliedPage` is the last page sent to
  // preview; it powers the live deep link. `previewReady` flips once the preview
  // endpoint is serving (a light poll). `published` hides the panel after a
  // successful go-live.
  const [appliedPage, setAppliedPage] = useState(null);
  const [previewReady, setPreviewReady] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);

  const endRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await getCmsAiStatus();
      if (cancelled) return;
      setEnabled(Boolean(s && s.enabled));
      setStatusLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const send = async (raw) => {
    const text = String(raw == null ? input : raw).trim();
    if (!text || thinking) return;
    setError(null);
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setThinking(true);
    try {
      const data = await sendCmsAiMessage({ state: convoState, message: text });
      setConvoState(data?.state || null);
      if (Array.isArray(data?.pages)) setPages(data.pages);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: data?.reply || '...',
          proposal: data?.proposal || null,
        },
      ]);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setThinking(false);
    }
  };

  // Approve a proposed change: commit it to the tenant's preview (uat) site.
  // Binds to the conversation's locked page (state.page) + this message's
  // proposed changes. On success, drop a chat message with the preview link.
  const approve = async (idx, proposal) => {
    const page = convoState?.page;
    if (!page || !page.path || applyingIdx !== null) return;
    if (
      !proposal ||
      !Array.isArray(proposal.changes) ||
      proposal.changes.length === 0
    )
      return;
    setError(null);
    setApplyingIdx(idx);
    try {
      const data = await applyCmsAiEdit({ page, changes: proposal.changes });
      if (data?.ok) {
        setAppliedIdx((prev) => new Set(prev).add(idx));
        // A new draft is now waiting to publish: reset publish state and show
        // the Publish panel for this page.
        setAppliedPage(page);
        setPreviewReady(false);
        setPublished(false);
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content:
              data.message ||
              'Your change is saved to your preview site and is deploying now. Give it a few minutes, then review it at the preview link.',
            link: data.previewUrl
              ? { url: data.previewUrl, label: 'Open preview site' }
              : null,
          },
        ]);
      } else {
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content:
              data?.message ||
              'I could not apply that change. Please try rewording it.',
          },
        ]);
      }
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setApplyingIdx(null);
    }
  };

  // Once a change is sent to preview, poll lightly until the preview endpoint is
  // serving, so we can tell the client "your preview is ready" and enable
  // Publish with confidence. Stops on ready, on publish, or on unmount.
  useEffect(() => {
    if (!appliedPage || previewReady || published) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      const st = await getCmsAiPreviewStatus({ slug: appliedPage.slug });
      if (cancelled) return;
      if (st && st.previewReady) {
        setPreviewReady(true);
        return;
      }
      timer = setTimeout(tick, 15000);
    };
    timer = setTimeout(tick, 8000);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [appliedPage, previewReady, published]);

  // Publish the reviewed draft to the live site (promote uat -> prod).
  const publish = async () => {
    if (!appliedPage || publishing || published) return;
    setError(null);
    setPublishing(true);
    try {
      const data = await promoteCmsAi({ page: appliedPage });
      if (data?.ok) {
        setPublished(true);
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content:
              data.message ||
              'Done. Your changes are now live on your website. It can take a few minutes to appear for everyone.',
            link: data.prodUrl
              ? { url: data.prodUrl, label: 'Open your live site' }
              : null,
          },
        ]);
      } else {
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content:
              data?.message ||
              'I could not publish just now. Please try again in a moment.',
          },
        ]);
      }
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setPublishing(false);
    }
  };

  // Show the page chips only while we are still choosing a page (before the
  // conversation has locked one in), as a quick alternative to typing.
  const showPageChips = useMemo(() => {
    const step = convoState?.step;
    return (
      pages.length > 0 && (!step || step === 'start' || step === 'need_page')
    );
  }, [pages, convoState]);

  if (statusLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!enabled) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <AutoAwesomeRoundedIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            UpRyzze AI
          </Typography>
        </Stack>
        <Alert severity="info" sx={{ mt: 2 }}>
          UpRyzze AI is not turned on for this site yet. In the meantime you can
          edit your content directly.
        </Alert>
        {slug ? (
          <Button
            component={RouterLink}
            to={`/business/${slug}/content`}
            variant="outlined"
            startIcon={<EditRoundedIcon />}
            sx={{ mt: 2 }}
          >
            Edit content
          </Button>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 860, mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeRoundedIcon color="primary" />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              UpRyzze AI
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Change your website by chatting. No tech skills needed.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <AiUsageMeter variant="compact" />
          {slug ? (
            <Button
              component={RouterLink}
              to={`/business/${slug}/content`}
              size="small"
              variant="outlined"
              startIcon={<EditRoundedIcon />}
            >
              Edit manually
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {/* Chat */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 },
          height: { xs: '64vh', md: '68vh' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
          <Stack spacing={1.25}>
            {messages.map((m, i) => (
              <Box
                key={i}
                sx={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor:
                      m.role === 'user' ? 'primary.main' : 'action.hover',
                    color:
                      m.role === 'user'
                        ? 'primary.contrastText'
                        : 'text.primary',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {m.content}
                  </Typography>
                </Box>
                {m.role === 'assistant' && m.proposal ? (
                  <ProposalPreview
                    proposal={m.proposal}
                    onApprove={() => approve(i, m.proposal)}
                    busy={applyingIdx === i}
                    applied={appliedIdx.has(i)}
                  />
                ) : null}
                {m.role === 'assistant' && m.link ? (
                  <Box sx={{ mt: 0.75 }}>
                    <Button
                      component="a"
                      href={m.link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewRoundedIcon />}
                    >
                      {m.link.label || 'Open preview'}
                    </Button>
                  </Box>
                ) : null}
              </Box>
            ))}

            {showPageChips ? (
              <Box sx={{ alignSelf: 'flex-start', pt: 0.5 }}>
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ flexWrap: 'wrap', gap: 0.75 }}
                >
                  {pages.slice(0, 12).map((p) => (
                    <Chip
                      key={`${p.collection}/${p.slug}`}
                      label={p.title}
                      size="small"
                      onClick={() => send(p.title)}
                      disabled={thinking}
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}

            {thinking ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ pl: 0.5, py: 0.5 }}
              >
                <CircularProgress size={14} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Thinking...
                </Typography>
              </Stack>
            ) : null}
            <div ref={endRef} />
          </Stack>
        </Box>

        {appliedPage && !published ? (
          <Paper
            variant="outlined"
            // `success.50` was a guess at a numeric shade MUI's default
            // palette does not ship, so it resolved to nothing and the panel
            // that is supposed to stand out as "ready to publish" rendered on
            // a plain background. Derive the tint from the palette instead, so
            // it is right in both light and dark mode.
            sx={{
              mt: 1,
              p: 1.25,
              borderColor: 'success.light',
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {previewReady
                  ? 'Your preview is ready. Happy with it? Publish it to your live site.'
                  : 'Your change is on your preview site (it may take a minute to deploy). Review it, then publish.'}
              </Typography>
              <Button
                variant="contained"
                color="success"
                onClick={publish}
                disabled={publishing}
                startIcon={
                  publishing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RocketLaunchRoundedIcon />
                  )
                }
                sx={{ whiteSpace: 'nowrap' }}
              >
                {publishing ? 'Publishing...' : 'Publish to my live site'}
              </Button>
            </Stack>
          </Paper>
        ) : null}

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Tell me what to change..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={thinking}
          />
          <Button
            variant="contained"
            onClick={() => send()}
            disabled={thinking || !input.trim()}
            sx={{ minWidth: 0, px: 1.5 }}
          >
            <SendRoundedIcon fontSize="small" />
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
